/**
 * Offline-Foto-Warteschlange.
 *
 * Speichert Foto-Blobs samt Beschreibung lokal in IndexedDB, falls beim
 * Aufnehmen kein Netz vorhanden ist (oder der Upload aus anderen Gründen
 * fehlschlägt). Sobald wieder Internet da ist, werden die Fotos automatisch
 * an den Server geschickt.
 *
 * Wird ausschließlich auf dem Client genutzt — keine Service-Worker-Magie,
 * lediglich die Online/Offline-Events des Browsers + ein erneuter Versuch
 * beim Seiten-Laden.
 */

import { PhotoApi } from "./photo-api";
import { queryClient } from "./queryClient";

const DB_NAME = "bauwerkplus-offline";
const DB_VERSION = 1;
const STORE = "pending-photos";

export interface PendingPhotoRecord {
  id: number;
  projectId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  description: string;
  blob: Blob;
  queuedAt: number;
  attempts: number;
  lastError?: string | null;
}

let dbPromise: Promise<IDBDatabase> | null = null;
let indexedDbAvailable: boolean | null = null;

function getIndexedDb(): IDBFactory | null {
  if (typeof indexedDB === "undefined") return null;
  return indexedDB;
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const idb = getIndexedDb();
  if (!idb) {
    dbPromise = Promise.reject(new Error("IndexedDB ist in dieser Umgebung nicht verfügbar"));
    return dbPromise;
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = idb.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("projectId", "projectId", { unique: false });
      }
    };
    req.onsuccess = () => {
      indexedDbAvailable = true;
      resolve(req.result);
    };
    req.onerror = () => {
      indexedDbAvailable = false;
      reject(req.error ?? new Error("IndexedDB konnte nicht geöffnet werden"));
    };
    req.onblocked = () => {
      reject(new Error("IndexedDB ist blockiert"));
    };
  });
  return dbPromise;
}

export function isOfflineQueueSupported(): boolean {
  return getIndexedDb() !== null;
}

// ---------------------------------------------------------------------------
// Change-Listener (für die UI)
// ---------------------------------------------------------------------------
const changeListeners = new Set<() => void>();
let bc: BroadcastChannel | null = null;
if (typeof window !== "undefined" && typeof BroadcastChannel !== "undefined") {
  try {
    bc = new BroadcastChannel("bauwerkplus-offline-queue");
    bc.onmessage = (ev) => {
      if (ev.data?.type === "queue-change") {
        changeListeners.forEach((l) => {
          try {
            l();
          } catch {
            /* ignore */
          }
        });
      }
    };
  } catch {
    bc = null;
  }
}

export function onQueueChange(cb: () => void): () => void {
  changeListeners.add(cb);
  return () => {
    changeListeners.delete(cb);
  };
}

function emitChange(): void {
  changeListeners.forEach((l) => {
    try {
      l();
    } catch {
      /* ignore */
    }
  });
  try {
    bc?.postMessage({ type: "queue-change" });
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// CRUD-Helfer
// ---------------------------------------------------------------------------
function runRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB-Anfrage fehlgeschlagen"));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  const transaction = db.transaction(STORE, mode);
  const store = transaction.objectStore(STORE);
  return runRequest<T>(fn(store));
}

export async function enqueuePhoto(input: {
  projectId: string;
  blob: Blob;
  fileName: string;
  mimeType: string;
  fileSize: number;
  description?: string;
}): Promise<PendingPhotoRecord> {
  const record: Omit<PendingPhotoRecord, "id"> = {
    projectId: input.projectId,
    blob: input.blob,
    fileName: input.fileName,
    mimeType: input.mimeType,
    fileSize: input.fileSize,
    description: input.description ?? "",
    queuedAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  const key = await withStore<IDBValidKey>("readwrite", (store) => store.add(record));
  emitChange();
  return { id: key as number, ...record };
}

export async function listPending(projectId?: string): Promise<PendingPhotoRecord[]> {
  try {
    const all = (await withStore<PendingPhotoRecord[]>("readonly", (store) =>
      store.getAll(),
    )) as PendingPhotoRecord[];
    const filtered = projectId ? all.filter((r) => r.projectId === projectId) : all;
    return filtered.sort((a, b) => a.queuedAt - b.queuedAt);
  } catch {
    return [];
  }
}

export async function countPending(projectId?: string): Promise<number> {
  if (!projectId) {
    try {
      return await withStore<number>("readonly", (store) => store.count());
    } catch {
      return 0;
    }
  }
  const items = await listPending(projectId);
  return items.length;
}

export async function updatePendingDescription(id: number, description: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE, "readwrite");
    const store = transaction.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const rec = getReq.result as PendingPhotoRecord | undefined;
      if (!rec) {
        resolve();
        return;
      }
      rec.description = description;
      const putReq = store.put(rec);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () =>
        reject(putReq.error ?? new Error("Beschreibung konnte nicht gespeichert werden"));
    };
    getReq.onerror = () =>
      reject(getReq.error ?? new Error("Eintrag konnte nicht geladen werden"));
  });
  emitChange();
}

export async function removePending(id: number): Promise<void> {
  await withStore<undefined>("readwrite", (store) => store.delete(id));
  emitChange();
}

async function markAttempt(id: number, err: unknown): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE, "readwrite");
      const store = transaction.objectStore(STORE);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const rec = getReq.result as PendingPhotoRecord | undefined;
        if (!rec) {
          resolve();
          return;
        }
        rec.attempts = (rec.attempts ?? 0) + 1;
        rec.lastError = err instanceof Error ? err.message : String(err);
        const putReq = store.put(rec);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () =>
          reject(putReq.error ?? new Error("Versuch konnte nicht protokolliert werden"));
      };
      getReq.onerror = () => reject(getReq.error ?? new Error("Eintrag fehlt"));
    });
  } catch {
    /* ignore — Protokollierung ist nice-to-have */
  }
}

// ---------------------------------------------------------------------------
// Flush-Logik
// ---------------------------------------------------------------------------
const flushingListeners = new Set<(b: boolean) => void>();
let flushing = false;
let pendingFlushPromise: Promise<{ uploaded: number; failed: number }> | null = null;

export function onFlushingChange(cb: (b: boolean) => void): () => void {
  flushingListeners.add(cb);
  cb(flushing);
  return () => {
    flushingListeners.delete(cb);
  };
}

function setFlushing(value: boolean): void {
  flushing = value;
  flushingListeners.forEach((l) => {
    try {
      l(value);
    } catch {
      /* ignore */
    }
  });
}

export function isFlushing(): boolean {
  return flushing;
}

export function isOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

export async function flushQueue(): Promise<{ uploaded: number; failed: number }> {
  if (!isOnline()) return { uploaded: 0, failed: 0 };
  if (pendingFlushPromise) return pendingFlushPromise;
  pendingFlushPromise = doFlush().finally(() => {
    pendingFlushPromise = null;
  });
  return pendingFlushPromise;
}

function blobToFile(blob: Blob, name: string, mime: string): File {
  if (blob instanceof File) return blob;
  return new File([blob], name, {
    type: mime || blob.type || "application/octet-stream",
  });
}

async function doFlush(): Promise<{ uploaded: number; failed: number }> {
  setFlushing(true);
  let uploaded = 0;
  let failed = 0;
  const touchedProjects = new Set<string>();
  try {
    const items = await listPending();
    for (const item of items) {
      if (!isOnline()) break;
      try {
        const file = blobToFile(item.blob, item.fileName, item.mimeType);
        const photo = await PhotoApi.uploadPhoto(item.projectId, file);
        if (item.description && item.description.trim().length > 0) {
          try {
            await PhotoApi.updateMetadata(photo.id, {
              description: item.description,
            });
          } catch {
            /* nicht fatal — Beschreibung kann später erneut gespeichert werden */
          }
        }
        await removePending(item.id);
        touchedProjects.add(item.projectId);
        uploaded += 1;
      } catch (err) {
        failed += 1;
        await markAttempt(item.id, err);
      }
    }
  } finally {
    setFlushing(false);
  }
  touchedProjects.forEach((projectId) => {
    try {
      queryClient.invalidateQueries({
        queryKey: ["/api/projects", projectId, "photos"],
      });
    } catch {
      /* ignore */
    }
  });
  return { uploaded, failed };
}

// ---------------------------------------------------------------------------
// Online-Listener (modulweit, einmalig)
// ---------------------------------------------------------------------------
let listenersInstalled = false;
export function ensureOnlineListener(): void {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;
  window.addEventListener("online", () => {
    flushQueue().catch(() => {
      /* swallow — UI zeigt Status separat */
    });
  });
  if (isOnline()) {
    countPending()
      .then((n) => {
        if (n > 0) {
          flushQueue().catch(() => {
            /* swallow */
          });
        }
      })
      .catch(() => {
        /* swallow */
      });
  }
}

export function isLikelyNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return (
      msg.includes("failed to fetch") ||
      msg.includes("network") ||
      msg.includes("connection") ||
      msg.includes("offline")
    );
  }
  return false;
}
